import React, { ReactElement, ReactNode, useState, JSX } from 'react';
import { observer } from 'mobx-react';
import PasswordChangeForm from './PasswordChangeForm';

import fallback from '../../../_Translations/generated/password-change.en.t.json';
import Keys from '../../../_Translations/generated/password-change.en.t.json.keys';
import Error from '../UI/Messages/Error';
import PasswordChangeFormType from './PasswordChangeFormType';
import useTranslation from '../../Hooks/useTranslation';
import useApi from '../../Hooks/useApi';
import Success from '../UI/Messages/Success';
import { buildContent } from '../../Modules/AgencyInfo/AgencyInfoModule';
import { useMount } from 'react-use';
import { LoggerFactory } from '@ibe/services';
const logger = LoggerFactory.get('PasswordChange');

export interface PasswordChangeProps {
  agency: string;
  login: string;
  token: string;
  onSuccess: () => void;
  render?: (Form: ReactNode) => ReactElement;
  loading?: (isLoading: boolean) => void;
  initializeFailedMessage?: boolean;
}

const PasswordChange = (props: PasswordChangeProps): JSX.Element => {
  const { t } = useTranslation('password-change', fallback, Keys);
  const { loading, initializeFailedMessage = true } = props;
  const api = useApi();
  const [passwordChangeFailedMessage, setPasswordChangeFailedMessage] = useState<
    string | undefined
  >(undefined);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState<boolean>(false);
  const [passwordComplexityInfo, setPasswordComplexityInfo] = useState<string | undefined>(
    undefined
  );

  useMount(async () => {
    if (loading) {
      loading(true);
    }
    try {
      const apiResponse = await api.getPasswordComplexity();
      if (apiResponse.validationEnabled) {
        setPasswordComplexityInfo(buildContent(apiResponse, t));
      }
    } catch (e) {
      logger.error(e);
    } finally {
      if (loading) {
        loading(false);
      }
    }
  });

  const onSubmit = async (values: PasswordChangeFormType): Promise<void> => {
    setPasswordChangeSuccess(false);
    setPasswordChangeFailedMessage(
      initializeFailedMessage ? t(Keys.passwordChangeFailed) : undefined
    );
    try {
      if (loading) {
        loading(true);
      }
      const apiResponse = await api.changePassword({
        agencyNumber: props.agency,
        login: props.login,
        token: props.token,
        newPassword: values.password
      });
      if (apiResponse.message) {
        setPasswordChangeFailedMessage(apiResponse.message);
      } else {
        setPasswordChangeSuccess(true);
        props.onSuccess();
      }
    } catch (e) {
      setPasswordChangeFailedMessage(t(Keys.passwordChangeFailed));
    } finally {
      if (loading) {
        loading(false);
      }
    }
  };

  const render =
    props.render ||
    ((Form: ReactNode): JSX.Element => {
      return <>{Form}</>;
    });

  const Form = observer(
    (): JSX.Element => {
      return (
        <>
          {passwordChangeFailedMessage && <Error message={passwordChangeFailedMessage} />}
          {passwordChangeSuccess && <Success message={t(Keys.passwordChangeSuccess)} />}
          <PasswordChangeForm onSubmit={onSubmit} passwordComplexityInfo={passwordComplexityInfo} />
        </>
      );
    }
  );

  return render(<Form />);
};

export default PasswordChange;
